<template>
  <div class="delivery-calendar" :class="{ embedded: embedded }">
    <!-- 顶部操作栏 -->
    <div class="cal-toolbar">
      <div class="cal-nav">
        <el-button circle size="small" icon="el-icon-arrow-left" @click="prevMonth" />
        <span class="cal-title">{{ displayYear }}年{{ displayMonth }}月</span>
        <el-button circle size="small" icon="el-icon-arrow-right" @click="nextMonth" />
      </div>
      <div class="cal-actions">
        <el-button size="small" @click="goToday">今天</el-button>
        <el-button size="small" type="primary" plain :loading="syncing" @click="syncOrderSchedules">
          批量排餐
        </el-button>
        <el-button size="small" icon="el-icon-download" @click="exportCalendar">导出日历</el-button>
      </div>
    </div>

    <el-alert
      v-if="conflictCount > 0"
      type="warning"
      show-icon
      :closable="false"
      class="conflict-alert"
      :title="`检测到 ${conflictCount} 笔未用餐排在非营业日，建议点击「批量排餐」一键重新排餐`"
    />

    <!-- 月视图 -->
    <div class="cal-grid-wrap" v-loading="loading">
      <transition name="cal-fade" mode="out-in">
        <div :key="monthKey" class="cal-grid">
          <div v-for="w in weekLabels" :key="w" class="cal-head">{{ w }}</div>
          <div
            v-for="(day, idx) in calendarCells"
            :key="monthKey + '-' + idx"
            class="cal-cell"
            :class="cellClass(day)"
            @click="day && openDayDrawer(day)"
            @mouseenter="hoverDay = day"
            @mouseleave="hoverDay = null"
          >
            <template v-if="day">
              <div class="cell-top">
                <span class="day-num" :class="{ today: day.isToday }">{{ day.day }}</span>
                <span v-if="day.badge" class="day-badge" :class="day.badgeClass">{{ day.badge }}</span>
              </div>
              <div v-if="day.holidayName && day.kind !== 'weekly_rest'" class="day-holiday-name">
                {{ day.holidayName }}
              </div>
              <div class="meal-tags">
                <span
                  v-for="tag in day.visibleTags"
                  :key="tag.key"
                  class="meal-tag"
                  :class="tag.cls"
                  :title="tag.title"
                >{{ tag.text }}</span>
                <span v-if="day.moreCount > 0" class="meal-tag more">+{{ day.moreCount }}</span>
              </div>
              <div v-if="hoverDay === day" class="cell-tip">
                {{ day.statusText }} · 午{{ day.meals.lunch.total }} / 晚{{ day.meals.dinner.total }}
              </div>
            </template>
          </div>
        </div>
      </transition>
    </div>

    <!-- 图例 -->
    <div class="cal-legend">
      <span><i class="lg work" />营业日</span>
      <span><i class="lg legal" />法定节假日</span>
      <span><i class="lg weekly" />每周休息日</span>
      <span><i class="lg makeup" />补班日</span>
      <span><i class="lg lunch" />午餐</span>
      <span><i class="lg dinner" />晚餐</span>
      <span><i class="lg verified" />已核销</span>
      <span><i class="lg deferred" />已延期</span>
    </div>

    <!-- 当日详情抽屉 -->
    <el-drawer
      :title="drawerTitle"
      :visible.sync="drawerVisible"
      size="380px"
      direction="rtl"
      custom-class="day-drawer"
    >
      <div v-if="activeDay" class="drawer-body">
        <div class="drawer-status">
          <el-tag :type="statusTagType(activeDay)" size="medium">{{ activeDay.statusText }}</el-tag>
          <span v-if="activeDay.holidayName" class="drawer-name">{{ activeDay.holidayName }}</span>
        </div>

        <div class="drawer-section">
          <div class="section-title">营业状态</div>
          <el-radio-group v-model="editDayType" size="small" @change="applyDayType">
            <el-radio-button label="auto">营业/按周休</el-radio-button>
            <el-radio-button label="holiday">休息日</el-radio-button>
            <el-radio-button label="makeup_workday">补班日</el-radio-button>
          </el-radio-group>
          <p class="drawer-hint">默认按中国法定节假日与调休：补班日自动排餐，法定假（如中秋）不排餐。</p>
        </div>

        <div class="drawer-section">
          <div class="section-title">餐次统计</div>
          <div class="stat-row">
            <span class="meal-tag lunch">午</span>
            <span>合计 {{ activeDay.meals.lunch.total }} · 待配送 {{ activeDay.meals.lunch.pending }} · 已核销 {{ activeDay.meals.lunch.verified }}</span>
          </div>
          <div class="stat-row">
            <span class="meal-tag dinner">晚</span>
            <span>合计 {{ activeDay.meals.dinner.total }} · 待配送 {{ activeDay.meals.dinner.pending }} · 已核销 {{ activeDay.meals.dinner.verified }}</span>
          </div>
          <p v-if="activeDay.isRest && (activeDay.meals.lunch.pending + activeDay.meals.dinner.pending) > 0" class="conflict-text">
            检测到排餐冲突：休息日仍有未用餐次，请使用「批量排餐」重新排期。
          </p>
        </div>

        <div class="drawer-actions">
          <el-button type="primary" :loading="syncing" @click="syncOrderSchedules">一键重新排餐</el-button>
        </div>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { formatDate } from '../utils/calendarUtils'
import { formatApiError } from '../utils/apiError'
import { triggerBlobDownload } from '../utils/fileExport'

const EMPTY_MEALS = () => ({
  lunch: { total: 0, verified: 0, deferred: 0, pending: 0 },
  dinner: { total: 0, verified: 0, deferred: 0, pending: 0 }
})

export default {
  name: 'SubscriptionCalendar',
  props: {
    embedded: { type: Boolean, default: false }
  },
  data() {
    return {
      currentYear: 2026,
      currentMonth: 0,
      overrides: {},
      dayMeta: {},
      mealsMap: {},
      conflictCount: 0,
      loading: false,
      syncing: false,
      hoverDay: null,
      drawerVisible: false,
      activeDay: null,
      editDayType: 'auto',
      weekLabels: ['一', '二', '三', '四', '五', '六', '日']
    }
  },
  computed: {
    displayYear() {
      return this.currentYear
    },
    displayMonth() {
      return this.currentMonth + 1
    },
    monthKey() {
      return `${this.currentYear}-${this.displayMonth}`
    },
    drawerTitle() {
      if (!this.activeDay) return '当日详情'
      return `${this.activeDay.dateStr} 餐次详情`
    },
    calendarCells() {
      const y = this.currentYear
      const m = this.currentMonth
      const first = new Date(y, m, 1)
      // 周一为第一列：周一=0 … 周日=6
      let startOffset = first.getDay() - 1
      if (startOffset < 0) startOffset = 6
      const daysInMonth = new Date(y, m + 1, 0).getDate()
      const prevDays = new Date(y, m, 0).getDate()
      const todayStr = formatDate(new Date())
      const cells = []

      for (let i = 0; i < startOffset; i++) {
        const dayNum = prevDays - startOffset + 1 + i
        const d = new Date(y, m - 1, dayNum)
        cells.push(this.buildDay(d, false, todayStr))
      }
      for (let i = 1; i <= daysInMonth; i++) {
        const d = new Date(y, m, i)
        cells.push(this.buildDay(d, true, todayStr))
      }
      while (cells.length % 7 !== 0) {
        const nextNum = cells.length - startOffset - daysInMonth + 1
        const d = new Date(y, m + 1, nextNum)
        cells.push(this.buildDay(d, false, todayStr))
      }
      return cells
    }
  },
  mounted() {
    const now = new Date()
    this.currentYear = now.getFullYear()
    this.currentMonth = now.getMonth()
    this.loadMonthOverrides()
  },
  methods: {
    buildDay(date, inMonth, todayStr) {
      const dateStr = formatDate(date)
      const meta = this.dayMeta[dateStr] || {}
      const override = this.overrides[dateStr]
      const meals = this.mealsMap[dateStr] || EMPTY_MEALS()
      let kind = meta.kind || 'workday'
      let holidayName = meta.name || ''
      let badge = meta.label || ''
      let isRest = !!meta.is_rest

      if (!meta.kind) {
        if (override === 'makeup_workday') {
          kind = 'makeup'
          badge = '班'
          isRest = false
          holidayName = holidayName || '调休补班'
        } else if (override === 'holiday') {
          kind = 'weekly_rest'
          badge = '休'
          isRest = true
        }
      }

      const tags = []
      if (meals.lunch.total > 0) {
        const verified = meals.lunch.verified >= meals.lunch.total
        tags.push({
          key: 'l',
          text: meals.lunch.total > 1 ? `午×${meals.lunch.total}` : '午',
          cls: verified ? 'verified' : 'lunch',
          title: `午餐 ${meals.lunch.total} 份`
        })
      }
      if (meals.dinner.total > 0) {
        const verified = meals.dinner.verified >= meals.dinner.total
        tags.push({
          key: 'd',
          text: meals.dinner.total > 1 ? `晚×${meals.dinner.total}` : '晚',
          cls: verified ? 'verified' : 'dinner',
          title: `晚餐 ${meals.dinner.total} 份`
        })
      }
      const visibleTags = tags.slice(0, 2)
      const moreCount = Math.max(0, tags.length - 2)

      let statusText = '营业日'
      if (kind === 'makeup') statusText = '补班日'
      else if (kind === 'legal_holiday') statusText = '法定节假日'
      else if (kind === 'custom_rest') statusText = '自定义休息'
      else if (kind === 'weekly_rest') statusText = '每周休息日'
      else if (isRest) statusText = '休息日'

      return {
        date,
        dateStr,
        day: date.getDate(),
        inMonth,
        isToday: dateStr === todayStr,
        kind,
        badge,
        badgeClass: kind === 'makeup' ? 'badge-makeup' : 'badge-rest',
        holidayName: kind === 'weekly_rest' ? '' : holidayName,
        isRest,
        meals,
        visibleTags,
        moreCount,
        statusText
      }
    },
    cellClass(day) {
      if (!day) return 'empty'
      return {
        'out-month': !day.inMonth,
        work: !day.isRest && day.kind !== 'makeup',
        legal: day.kind === 'legal_holiday' || day.kind === 'custom_rest',
        weekly: day.kind === 'weekly_rest',
        makeup: day.kind === 'makeup',
        'has-conflict':
          day.isRest && day.inMonth && day.meals.lunch.pending + day.meals.dinner.pending > 0
      }
    },
    statusTagType(day) {
      if (day.kind === 'makeup') return 'primary'
      if (day.isRest) return 'danger'
      return 'success'
    },
    async loadMonthOverrides() {
      this.loading = true
      try {
        const resp = await this.$api.get('/admin/meal-schedules/calendar-month', {
          params: { year: this.currentYear, month: this.displayMonth }
        })
        if (resp && resp.code === 200 && resp.data) {
          this.overrides = resp.data.overrides || {}
          this.dayMeta = resp.data.days || {}
          this.mealsMap = resp.data.meals || {}
          this.conflictCount = Number(resp.data.conflict_count || 0)
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '加载日历配置失败'))
      } finally {
        this.loading = false
      }
    },
    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentYear--
        this.currentMonth = 11
      } else {
        this.currentMonth--
      }
      this.loadMonthOverrides()
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentYear++
        this.currentMonth = 0
      } else {
        this.currentMonth++
      }
      this.loadMonthOverrides()
    },
    goToday() {
      const now = new Date()
      this.currentYear = now.getFullYear()
      this.currentMonth = now.getMonth()
      this.loadMonthOverrides()
    },
    openDayDrawer(day) {
      if (!day || !day.inMonth) return
      this.activeDay = day
      if (day.kind === 'makeup') this.editDayType = 'makeup_workday'
      else if (day.kind === 'legal_holiday' || day.kind === 'custom_rest') this.editDayType = 'holiday'
      else this.editDayType = 'auto'
      this.drawerVisible = true
    },
    async applyDayType(val) {
      if (!this.activeDay) return
      try {
        const resp = await this.$api.put('/admin/meal-schedules/holidays/day', {
          config_date: this.activeDay.dateStr,
          day_type: val
        })
        const realign = resp && resp.realign
        const extra =
          realign && realign.subscriptions > 0
            ? `，已同步 ${realign.subscriptions} 个订单排餐`
            : ''
        this.$message.success(`已更新营业状态${extra}`)
        await this.loadMonthOverrides()
        const refreshed = this.calendarCells.find(
          (d) => d && d.inMonth && d.dateStr === this.activeDay.dateStr
        )
        if (refreshed) this.activeDay = refreshed
      } catch (e) {
        this.$message.error(formatApiError(e, '更新失败'))
      }
    },
    async syncOrderSchedules() {
      this.syncing = true
      try {
        const resp = await this.$api.post('/admin/meal-schedules/realign-holidays')
        if (resp && resp.code === 200) {
          const d = resp.data || {}
          this.$message.success(
            resp.message || `已同步 ${d.subscriptions || 0} 个订阅，调整 ${d.shifted || 0} 餐次`
          )
          await this.loadMonthOverrides()
        } else {
          this.$message.error((resp && resp.message) || '同步失败')
        }
      } catch (e) {
        this.$message.error(formatApiError(e, '同步失败'))
      } finally {
        this.syncing = false
      }
    },
    exportCalendar() {
      const lines = ['日期,星期,类型,名称,是否营业,午餐份数,晚餐份数']
      const weekNames = ['日', '一', '二', '三', '四', '五', '六']
      this.calendarCells
        .filter((d) => d && d.inMonth)
        .forEach((d) => {
          lines.push(
            [
              d.dateStr,
              weekNames[d.date.getDay()],
              d.statusText,
              d.holidayName || '',
              d.isRest ? '否' : '是',
              d.meals.lunch.total,
              d.meals.dinner.total
            ].join(',')
          )
        })
      const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' })
      triggerBlobDownload(blob, `配送日历-${this.monthKey}`, 'csv')
    }
  }
}
</script>

<style scoped>
.delivery-calendar {
  padding: 8px 4px 20px;
}
.delivery-calendar.embedded {
  padding: 0;
}

.cal-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.cal-nav {
  display: flex;
  align-items: center;
  gap: 12px;
}
.cal-title {
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  min-width: 140px;
  text-align: center;
}
.cal-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.conflict-alert {
  margin-bottom: 14px;
}

.cal-grid-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(72px, 1fr));
  gap: 6px;
  min-width: 560px;
}
.cal-head {
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  padding: 4px 0;
}

.cal-cell {
  position: relative;
  min-height: 64px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 6px;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.15s;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.cal-cell:hover {
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
  transform: translateY(-1px);
}
.cal-cell.out-month {
  opacity: 0.38;
  cursor: default;
}
.cal-cell.legal {
  background: #fef2f2;
  border-color: #fecaca;
}
.cal-cell.weekly {
  background: #f8fafc;
  border-color: #e2e8f0;
}
.cal-cell.makeup {
  background: #eff6ff;
  border-color: #bfdbfe;
}
.cal-cell.has-conflict {
  box-shadow: inset 0 0 0 2px #f59e0b;
}

.cell-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}
.day-num {
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  width: 22px;
  height: 22px;
  line-height: 22px;
  text-align: center;
  border-radius: 50%;
}
.day-num.today {
  border: 2px solid #10b981;
  color: #059669;
}
.cal-cell.legal .day-num {
  color: #dc2626;
}
.cal-cell.weekly .day-num {
  color: #94a3b8;
}
.cal-cell.makeup .day-num {
  color: #2563eb;
}

.day-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 0 5px;
  border-radius: 5px;
  line-height: 1.5;
}
.badge-rest {
  background: #fecaca;
  color: #b91c1c;
}
.badge-makeup {
  background: #bfdbfe;
  color: #1d4ed8;
}
.day-holiday-name {
  font-size: 11px;
  color: #dc2626;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.meal-tags {
  margin-top: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}
.meal-tag {
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.35;
}
.meal-tag.lunch {
  background: #d1fae5;
  color: #047857;
}
.meal-tag.dinner {
  background: #ffedd5;
  color: #c2410c;
}
.meal-tag.verified {
  background: #e2e8f0;
  color: #64748b;
  text-decoration: line-through;
}
.meal-tag.deferred {
  background: #ede9fe;
  color: #6d28d9;
}
.meal-tag.more {
  background: #f1f5f9;
  color: #64748b;
}

.cell-tip {
  position: absolute;
  left: 6px;
  right: 6px;
  bottom: 4px;
  font-size: 10px;
  color: #64748b;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 6px;
  padding: 2px 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
}

.cal-legend {
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 14px 18px;
  font-size: 12px;
  color: #64748b;
}
.cal-legend .lg {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  margin-right: 6px;
  vertical-align: -1px;
}
.lg.work { background: #fff; border: 1px solid #cbd5e1; }
.lg.legal { background: #fecaca; }
.lg.weekly { background: #e2e8f0; }
.lg.makeup { background: #bfdbfe; }
.lg.lunch { background: #6ee7b7; }
.lg.dinner { background: #fdba74; }
.lg.verified { background: #94a3b8; }
.lg.deferred { background: #c4b5fd; }

.drawer-body { padding: 0 8px 24px; }
.drawer-status { display: flex; align-items: center; gap: 10px; margin-bottom: 20px; }
.drawer-name { color: #64748b; font-size: 14px; }
.drawer-section { margin-bottom: 22px; }
.section-title {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
  margin-bottom: 10px;
}
.drawer-hint {
  margin: 10px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #94a3b8;
}
.stat-row {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #475569;
  margin-bottom: 8px;
}
.conflict-text {
  margin-top: 10px;
  font-size: 12px;
  color: #d97706;
  line-height: 1.5;
}
.drawer-actions { margin-top: 12px; }

.cal-fade-enter-active,
.cal-fade-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
}
.cal-fade-enter,
.cal-fade-leave-to {
  opacity: 0;
  transform: translateX(8px);
}

@media (max-width: 768px) {
  .cal-title { font-size: 16px; min-width: 110px; }
  .cal-grid { gap: 4px; min-width: 480px; }
  .cal-cell { min-height: 56px; padding: 4px; }
}
</style>
